import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { authClient } from "@/lib/auth-client";
import { toast } from "sonner";

export const Route = createFileRoute("/forgot-password")({
  component: ForgotPassword,
});

function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const isEmail = (value: string) =>
    /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value);

  const handleSubmit = async () => {
    const v = email.trim();
    if (!isEmail(v)) {
      setEmailError("please enter a valid email address");
      return;
    }

    const redirectTo = `${window.location.origin}/reset-password`;

    await authClient.requestPasswordReset(
      { email: v, redirectTo },
      {
        onRequest: () => setLoading(true),
        onResponse: () => setLoading(false),
        onSuccess: () => {
          setSent(true);
          toast.success("reset link sent to your email");
        },
      }
    );
  };

  return (
    <div className="flex min-h-svh items-center justify-center p-4">
      <div className="flex w-full flex-col items-center gap-6">
        <img
          src="/header-logo.png"
          alt="clove.moe"
          className="h-12 w-auto align-middle"
        />
        <Card className="z-50 w-full max-w-md rounded-md shadow-lg bg-white">
          <CardHeader>
            <CardTitle className="text-lg md:text-xl text-[#353535]">
              forgot password
            </CardTitle>
            <CardDescription className="text-xs md:text-sm text-accent">
              enter your email and we will send you a reset link
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sent ? (
              <div className="grid gap-4">
                <p className="text-sm text-[#353535]">
                  if you have an account, you should receive a reset link in a
                  moment
                </p>
                <p className="text-xs text-gray-500">
                  if you dont receive a reset link, check your spam folder or
                  other folders. if you still dont receive it, try again or
                  contact support.
                </p>
              </div>
            ) : (
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="email" className="text-[#353535]">
                    email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="me@example.com"
                    value={email}
                    onChange={(e) => {
                      const v = e.target.value;
                      setEmail(v);
                      if (emailError && v.trim().length > 0)
                        setEmailError(null);
                    }}
                    aria-invalid={Boolean(emailError) || undefined}
                    aria-describedby={emailError ? "email-error" : undefined}
                    className={`${emailError ? "border-red-500 focus-visible:ring-red-500" : "focus-visible:ring-accent"}`}
                  />
                  {emailError && (
                    <p id="email-error" className="text-xs text-red-600 mt-1">
                      {emailError}
                    </p>
                  )}
                </div>
                <Button
                  type="button"
                  className="w-full bg-accent text-white hover:opacity-90"
                  disabled={loading}
                  onClick={handleSubmit}
                >
                  {loading ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    "send reset link"
                  )}
                </Button>
              </div>
            )}
          </CardContent>
          <CardFooter>
            <div className="flex justify-center w-full border-t pt-4">
              <div className="flex items-center gap-2">
                <a
                  href="/sign-in"
                  className="inline-block text-sm text-accent underline"
                >
                  back to sign in
                </a>
              </div>
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
